// dependency-graph-ignore-file
import { getBaseUrl } from '@databases/database-state';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type PanelManager } from '@databases/panel-types';
import { type SelectionsApi } from '@databases/selections-api';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type SelectionsActions } from '@databases/selections-api-types';
import { DatabaseFieldType } from '@databases/types/database';
import { getDatabasePath } from '@databases/utils/urls';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { type SelectionsEffectCellContextMenuContext } from '../select-value-menu/types';

export function createSelectValueMenuContext({
  canEditContent,
  canEditFields,
  closeMenu,
  contentId,
  currentViewId,
  databaseAdapter,
  deletePreviewLayer,
  entryId,
  fieldId,
  formatMessage,
  isDBAiFirstCreationEnabled = false,
  isDeletionDisabled = false,
  panel,
  panelManager,
  resolveEntryIdToRowIndex,
  resolveFieldIdToColIndex,
  selectionsActions,
  selectionsApi,
}: {
  canEditContent: boolean;
  canEditFields: boolean;
  closeMenu: () => void;
  contentId: string;
  currentViewId: string;
  databaseAdapter: DatabaseAdapter;
  deletePreviewLayer?: SelectionsEffectCellContextMenuContext['deletePreviewLayer'];
  entryId: string;
  fieldId: string;
  formatMessage: MessageFormatter;
  isDBAiFirstCreationEnabled?: boolean;
  isDeletionDisabled?: boolean;
  panel?: boolean;
  panelManager: PanelManager;
  resolveEntryIdToRowIndex?: (entryId: string) => number;
  resolveFieldIdToColIndex?: (fieldId: string) => number;
  selectionsActions: SelectionsActions;
  selectionsApi: SelectionsApi;
}): SelectionsEffectCellContextMenuContext | null {
  const isSelected = selectionsApi.isValueSelected(entryId, fieldId);

  if (!isSelected) {
    return null;
  }

  const selection = selectionsApi.getResolvedSelection();

  if (selection.mode !== 'cell') {
    return null;
  }

  if (selection.values.length !== 1) {
    return null;
  }

  const resolvedValue = selection.values[0];

  const canAskRovo =
    isDBAiFirstCreationEnabled &&
    canEditContent &&
    getExperimentValue('databasesAIFC', 'enabled', {
      fireExperimentExposure: false,
    });
  const canInsertContent = canEditContent;
  const canDeleteEntries = canEditContent;
  const canDeleteFields = canEditFields && !isDeletionDisabled;
  const canOpenComments = true;

  const canEditValue = databaseAdapter.canEditValue(
    resolvedValue.fieldId,
    resolvedValue.entryId
  );

  const canClearContent =
    canInsertContent &&
    canEditValue &&
    resolvedValue.field.type !== DatabaseFieldType.PAGE_STATUS;

  return {
    baseUrl: getBaseUrl(),
    capabilities: {
      canAskRovo,
      canClearContent,
      canDeleteEntries,
      canDeleteFields,
      canInsertContent,
      canOpenComments,
    },
    closeMenu,
    contentId,
    currentViewId,
    databasePath: getDatabasePath({
      spaceKey: databaseAdapter.getSpaceKey(),
      contentId,
    }),
    deletePreviewLayer,
    entryId,
    fieldId,
    formatMessage,
    panelManager,
    panel,
    resolveEntryIdToRowIndex,
    resolveFieldIdToColIndex,
    selectionsActions,
  };
}
